
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Chip, OutlinedInput, Checkbox, IconButton, Tooltip, Alert,
    


} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

// Define your backend URL (Ensure your backend is running on this port)
const API_URL = `${process.env.REACT_APP_API_URL}/api/therapy`; 

const TherapyAllotment = () => {
  // --- 1. Student Roster State (Clinicians) ---
  const [students, setStudents] = useState([]);
  const [editingStudentIndex, setEditingStudentIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
      const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
      const [openPasswordModal, setOpenPasswordModal] = useState(false);
      const [password, setPassword] = useState('');
      const [downloadFormat, setDownloadFormat] = useState('excel');

  // --- 2. Supervisor List State ---
  const [supervisors, setSupervisors] = useState([]);
  const [editingSupervisorIndex, setEditingSupervisorIndex] = useState(-1);

  // --- 3. Therapy Slots State (Updated for multiple clinicians and supervisors) ---
  const [therapySlots, setTherapySlots] = useState({
    unit: '', client_id: '', client_name: '', diagnosis: '', device: '', language: [], 
    supervisor_names: [], clinicianCourse: '', clinicianYear: '', clinicianNames: [], room: '',
    fromDate: '', toDate: '', days_config: '', timings: '', session: ''
  });

  // --- 4. Attendance State ---
  const [attendance, setAttendance] = useState({
    unit: '', startDate: '', endDate: '', 
    fetchedPatients: []
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, index: null, type: '', id: null });
  const [alertMessage, setAlertMessage] = useState({ open: false, message: '', severity: 'success' });

  // --- Options Constants ---
  const courseOptions = ['UG', 'PG'];
  const ugYearOptions = ['1st', '2nd', '3rd','Intern'];
  const pgYearOptions = ['1st', '2nd'];
  const sessionOptions = ['Morning', 'Afternoon'];
  const languageOptions = [
    'Kannada', 'Hindi', 'Malayalam', 'Telugu', 'Tamil', 'English', 
    'Marathi', 'Bengali', 'Gujarati', 'Punjabi', 'Oriya'
  ];
  const unitOptions = ['CLTU', 'Tinnitus', 'Vestibular'];
  const deviceOptions = ['None', 'HA', 'CI', 'CI+HA'];
  const dayOptions = [
    'Mon/ Wed/ Fri',
    'Tue/ Thu',
    'Tue/ Fri (Afternoon - Enabled)',
    'Tue/ Fri/ Sat (Moning- Enabled )'
  ];
  const timeSlots = [
    '9:15 to 10:00', '10:00 to 10:45', '10:45 to 11:30', '11:30 to 12:15',
    '1:15 to 2:00', '2:00 to 2:45', '2:45 to 3:30', '3:30 to 4:15'
  ];

  // Utility functions for rendering dropdown values
  const renderValue = (selected, placeholder) => selected || <em>{placeholder}</em>;
  const renderMultiChipValue = (selected) => {
    if (selected.length === 0) return <em>Select Languages</em>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };

  const renderMultiClinicianValue = (selected) => {
    if (selected.length === 0) return <em>Select Clinicians</em>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };

  const renderMultiSupervisorValue = (selected) => {
    if (selected.length === 0) return <em>Select Supervisors</em>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };
  
  // --- FETCH INITIAL DATA (useEffect & Callbacks) ---

  const fetchStudents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error("Error fetching students:", error);
      setAlertMessage({ open: true, message: 'Failed to fetch students from server.', severity: 'error' });
    }
  }, []);

  const fetchSupervisors = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/supervisors`);
      setSupervisors(response.data);
    } catch (error) {
      console.error("Error fetching supervisors:", error);
      setAlertMessage({ open: true, message: 'Failed to fetch supervisors from server.', severity: 'error' });
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchSupervisors();
  }, [fetchStudents, fetchSupervisors]);
  
  // --- Student Roster Handlers (CRUD) ---

  const handleStudentChange = (index, field, value) => {
    const updatedStudents = [...students];
    updatedStudents[index][field] = value;
    if (field === 'course') updatedStudents[index].year = '';
    setStudents(updatedStudents);
  };

  const addStudent = () => {
    setStudents(prev => [...prev, { id: null, student_id: '', name: '', course: '', year: '', session: '', start_date: '', end_date: '', languages: [] }]);
    setEditingStudentIndex(students.length);
  };
  
  const saveStudent = async (index) => {
    const student = students[index];
    const { id, name, student_id, course, year, session, start_date, end_date, languages } = student;
    
    if (!name || !student_id || !course || !year) {
        setAlertMessage({ open: true, message: 'Missing essential student fields (Name, ID, Course, Year).', severity: 'warning' });
        return;
    }
    
    // Determine if it's an update (ID exists) or a new creation
    const method = id ? 'put' : 'post';
    const url = id ? `${API_URL}/students/${id}` : `${API_URL}/students`;

    try {
      const response = await axios[method](url, {
        student_id, name, course, year, session, start_date, end_date, languages
      });
      
      const updatedStudents = [...students];
      updatedStudents[index] = response.data.data;
      setStudents(updatedStudents);
      setEditingStudentIndex(-1);
      setAlertMessage({ open: true, message: response.data.message, severity: 'success' });

    } catch (error) {
      const message = error.response?.data?.message || `Error ${id ? 'updating' : 'saving'} student.`;
      setAlertMessage({ open: true, message: message, severity: 'error' });
      
      // If saving new student fails, remove the temporary row
      if (!id) setStudents(prev => prev.slice(0, -1));
      setEditingStudentIndex(-1);
    }
  };

  const deleteStudent = async (studentId) => {
    try {
        const response = await axios.delete(`${API_URL}/students/${studentId}`);
        setStudents(prev => prev.filter(s => s.id !== studentId));
        setAlertMessage({ open: true, message: response.data.message, severity: 'success' });
    } catch (error) {
        const message = error.response?.data?.message || 'Error deleting student. Check if they are assigned to a slot.';
        setAlertMessage({ open: true, message: message, severity: 'error' });
    }
    setDeleteDialog({ open: false, index: null, type: '', id: null });
  };
  
  const toggleStudentEdit = (index) => {
    if (editingStudentIndex === index) {
      saveStudent(index);
    } else {
      setEditingStudentIndex(index);
    }
  };

  // --- Supervisor List Handlers (CRUD) ---

  const handleSupervisorChange = (index, field, value) => {
    const updatedSupervisors = [...supervisors];
    updatedSupervisors[index][field] = value;
    setSupervisors(updatedSupervisors);
  };

  const addSupervisor = () => {
    setSupervisors(prev => [...prev, { id: null, name: '', languages: [] }]);
    setEditingSupervisorIndex(supervisors.length);
  };

  const saveSupervisor = async (index) => {
    const supervisor = supervisors[index];
    const { id, name, languages } = supervisor;

    if (!name) {
        setAlertMessage({ open: true, message: 'Supervisor name is required.', severity: 'warning' });
        return;
    }
    
    const method = id ? 'put' : 'post';
    const url = id ? `${API_URL}/supervisors/${id}` : `${API_URL}/supervisors`;

    try {
      const response = await axios[method](url, { name, languages });
      
      const updatedSupervisors = [...supervisors];
      updatedSupervisors[index] = response.data.data;
      setSupervisors(updatedSupervisors);
      setEditingSupervisorIndex(-1);
      setAlertMessage({ open: true, message: response.data.message, severity: 'success' });
      
    } catch (error) {
      const message = error.response?.data?.message || `Error ${id ? 'updating' : 'saving'} supervisor.`;
      setAlertMessage({ open: true, message: message, severity: 'error' });

      if (!id) setSupervisors(prev => prev.slice(0, -1));
      setEditingSupervisorIndex(-1);
    }
  };

  const deleteSupervisor = async (supervisorId) => {
    try {
        const response = await axios.delete(`${API_URL}/supervisors/${supervisorId}`);
        setSupervisors(prev => prev.filter(s => s.id !== supervisorId));
        setAlertMessage({ open: true, message: response.data.message, severity: 'success' });
    } catch (error) {
        const message = error.response?.data?.message || 'Error deleting supervisor. Check if they are assigned to a slot.';
        setAlertMessage({ open: true, message: message, severity: 'error' });
    }
    setDeleteDialog({ open: false, index: null, type: '', id: null });
  };

  const toggleSupervisorEdit = (index) => {
    if (editingSupervisorIndex === index) {
      saveSupervisor(index);
    } else {
      setEditingSupervisorIndex(index);
    }
  };

  // --- Therapy Slots Handlers ---

  const handleTherapySlotsChange = (field, value) => {
    console.log(`Therapy Slots Change - Field: ${field}, Value:`, value);
    setTherapySlots(prev => ({ ...prev, [field]: value }));
  };

  const handleTherapySlotsSubmit = async () => {
    console.log('Submitting therapy slots:', therapySlots);
    
    // Check for essential fields (using the same field names as backend)
    if (!therapySlots.unit || !therapySlots.client_id || !therapySlots.clinicianNames || 
        therapySlots.clinicianNames.length === 0 || !therapySlots.room || !therapySlots.days_config || 
        !therapySlots.timings || !therapySlots.fromDate || !therapySlots.toDate || therapySlots.language.length === 0) {
        
        console.log('Missing fields:', {
            unit: !therapySlots.unit,
            client_id: !therapySlots.client_id,
            clinicianNames: !therapySlots.clinicianNames || therapySlots.clinicianNames.length === 0,
            room: !therapySlots.room,
            days_config: !therapySlots.days_config,
            timings: !therapySlots.timings,
            fromDate: !therapySlots.fromDate,
            toDate: !therapySlots.toDate,
            language: therapySlots.language.length === 0
        });
        
        setAlertMessage({ 
            open: true, 
            message: 'Please fill all essential fields (Unit, Client ID, Clinicians, Room, Schedule, Dates, Language).', 
            severity: 'warning' 
        });
        return;
    }

    try {
        // Prepare data exactly as backend expects
        const slotData = {
            unit: therapySlots.unit,
            client_id: therapySlots.client_id,
            client_name: therapySlots.client_name,
            diagnosis: therapySlots.diagnosis,
            device: therapySlots.device,
            language: therapySlots.language,
            supervisor_names: therapySlots.supervisor_names,
            clinicianCourse: therapySlots.clinicianCourse,
            clinicianYear: therapySlots.clinicianYear,
            clinicianNames: therapySlots.clinicianNames,
            room: therapySlots.room,
            fromDate: therapySlots.fromDate,
            toDate: therapySlots.toDate,
            days_config: therapySlots.days_config,
            timings: therapySlots.timings,
            session: therapySlots.session
        };

        console.log('Sending therapy slot data:', slotData);
        const response = await axios.post(`${API_URL}/slot`, slotData);
        
        setAlertMessage({ open: true, message: response.data.message, severity: 'success' });
        
        // Reset form on success
        setTherapySlots({
            unit: '', client_id: '', client_name: '', diagnosis: '', device: '', language: [], 
            supervisor_names: [], clinicianCourse: '', clinicianYear: '', clinicianNames: [], room: '',
            fromDate: '', toDate: '', days_config: '', timings: '', session: ''
        });
    } catch (error) {
        console.error('Error saving therapy slot:', error);
        const message = error.response?.data?.message || 'Error saving therapy slot or occupancy conflict.';
        setAlertMessage({ open: true, message: message, severity: 'error' });
    }
  };

  // --- Attendance Handlers ---
  
  const handleAttendanceStartDateChange = (e) => {
    const startVal = e.target.value;
    if (!startVal) {
      setAttendance(prev => ({ ...prev, startDate: '', endDate: '' }));
      return;
    }
    
    const startDate = new Date(startVal + 'T00:00:00'); 
    const lastDay = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
    const formattedEnd = lastDay.toISOString().split('T')[0];

    setAttendance(prev => ({
      ...prev,
      startDate: startVal,
      endDate: formattedEnd
    }));
  };
  
  const handleAttendanceUnitChange = (e) => {
      setAttendance(prev => ({ ...prev, unit: e.target.value }));
  };

  const fetchPatientDetails = async () => {
    if (!attendance.unit || !attendance.startDate || !attendance.endDate) {
      setAlertMessage({ open: true, message: 'Please select Unit, Start Date, and End Date to fetch details.', severity: 'warning' });
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/attendance-patients`, {
        params: {
          unit: attendance.unit,
          startDate: attendance.startDate,
          endDate: attendance.endDate
        }
      });
      if (response.data.length === 0) {
        setAlertMessage({ open: true, message: 'No patients found scheduled for this unit within the selected period.', severity: 'info' });
      }
      const patientsWithState = response.data.map(p => ({ ...p, isPresent: false }));
      setAttendance(prev => ({ ...prev, fetchedPatients: patientsWithState }));
    } catch (error) {
      const message = error.response?.data?.message || 'Error fetching patients for attendance.';
      setAlertMessage({ open: true, message: message, severity: 'error' });
      setAttendance(prev => ({ ...prev, fetchedPatients: [] }));
    }
  };

  const toggleAttendanceStatus = (index) => {
    const updatedPatients = [...attendance.fetchedPatients];
    updatedPatients[index].isPresent = !updatedPatients[index].isPresent;
    setAttendance(prev => ({ ...prev, fetchedPatients: updatedPatients }));
  };

  const handleSaveAttendance = async () => {
    if (attendance.fetchedPatients.length === 0) return;

    try {
      const attendanceData = {
        unit: attendance.unit,
        attendance_date: attendance.startDate, 
        patientData: attendance.fetchedPatients.map(p => ({
            client_id: p.client_id, 
            client_name: p.client_name,
            isPresent: p.isPresent,
        }))
      };
      const response = await axios.post(`${API_URL}/attendance`, attendanceData); 
      setAlertMessage({ open: true, message: response.data.message, severity: 'success' });
      setAttendance(prev => ({ ...prev, fetchedPatients: [] }));
    } catch (error) {
      const message = error.response?.data?.message || 'Error saving attendance.';
      setAlertMessage({ open: true, message: message, severity: 'error' });
    }
  };

  // --- Common Delete Handlers ---
  const handleOpenDeleteDialog = (id, type, index) => { 
      setDeleteDialog({ open: true, id, type, index }); 
  };
  
  const handleDeleteConfirm = () => {
    if (deleteDialog.type === 'student') {
        if (deleteDialog.id) {
            deleteStudent(deleteDialog.id);
        } else {
            setStudents(prev => prev.filter((_, i) => i !== deleteDialog.index));
            setDeleteDialog({ open: false, index: null, type: '', id: null });
        }
    } else if (deleteDialog.type === 'supervisor') {
        if (deleteDialog.id) {
            deleteSupervisor(deleteDialog.id);
        } else {
            setSupervisors(prev => prev.filter((_, i) => i !== deleteDialog.index));
            setDeleteDialog({ open: false, index: null, type: '', id: null });
        }
    }
  };
 const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Therapy Slots ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "therapy_slots",
          formName: "Therapy_Slots",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Therapy_Slots_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, margin: 'auto' }}>
       <Box sx={{ position: 'absolute', top: 310, right: 140 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center" sx={{ mb: 4 }}>
        Therapy Allotment 🗓️
      </Typography>
      
      {/* Alert Component */}
      {alertMessage.open && (
        <Alert 
          severity={alertMessage.severity} 
          onClose={() => setAlertMessage({ open: false, message: '', severity: 'success' })} 
          sx={{ mb: 2 }}
        >
          {alertMessage.message}
        </Alert>
      )}

      {/* --- 1. STUDENT ROSTER SECTION --- */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">Student Roster</Typography>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Name</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Languages</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student, index) => {
                const isEditing = editingStudentIndex === index;
                const studentId = student.id; 
                return (
                  <TableRow key={studentId || `new-${index}`} hover>
                  
                    <TableCell sx={{ minWidth: 180 }}> 
                        {isEditing ? (
                            <TextField size="small" fullWidth value={student.name} onChange={(e) => handleStudentChange(index, 'name', e.target.value)} />
                        ) : student.name}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}> 
                        {isEditing ? (
                            <TextField size="small" fullWidth value={student.student_id} onChange={(e) => handleStudentChange(index, 'student_id', e.target.value)} disabled={!!studentId} />
                        ) : student.student_id}
                    </TableCell>

                    <TableCell>
                       {isEditing ? (
                        <FormControl size="small" fullWidth>
                          <Select value={student.course} onChange={(e) => handleStudentChange(index, 'course', e.target.value)} displayEmpty renderValue={(s) => renderValue(s, 'Select Course')} InputLabelProps={{ shrink: true }}>
                            {courseOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                       ) : student.course}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" fullWidth>
                          <Select value={student.year} disabled={!student.course} onChange={(e) => handleStudentChange(index, 'year', e.target.value)} displayEmpty renderValue={(s) => renderValue(s, 'Select Year')} InputLabelProps={{ shrink: true }}>
                            {(student.course === 'UG' ? ugYearOptions : pgYearOptions).map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : student.year}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" fullWidth>
                          <Select value={student.session} onChange={(e) => handleStudentChange(index, 'session', e.target.value)} displayEmpty renderValue={(s) => renderValue(s, 'Select Session')} InputLabelProps={{ shrink: true }}>
                            {sessionOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : student.session}
                    </TableCell>
                    <TableCell>{isEditing ? (<TextField size="small" fullWidth type="date" value={student.start_date} onChange={(e) => handleStudentChange(index, 'start_date', e.target.value)} InputLabelProps={{ shrink: true }} />) : student.start_date}</TableCell>
                    <TableCell>{isEditing ? (<TextField size="small" fullWidth type="date" value={student.end_date} onChange={(e) => handleStudentChange(index, 'end_date', e.target.value)} InputLabelProps={{ shrink: true }} />) : student.end_date}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl size="small" fullWidth>
                          <Select multiple value={student.languages} onChange={(e) => handleStudentChange(index, 'languages', e.target.value)} renderValue={renderMultiChipValue} displayEmpty InputLabelProps={{ shrink: true }}>
                            {languageOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : (student.languages || []).join(', ')}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        <Tooltip title={isEditing ? "Save" : "Edit"}>
                            <IconButton onClick={() => toggleStudentEdit(index)} color="primary" size="small">
                                {isEditing ? <SaveIcon /> : <EditIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton onClick={() => handleOpenDeleteDialog(studentId, 'student', index)} color="error" size="small" disabled={isEditing}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Button variant="outlined" onClick={addStudent} disabled={editingStudentIndex !== -1}>Add Student</Button>
      </Paper>

      {/* --- 2. SUPERVISOR LIST SECTION --- */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">Supervisor List</Typography>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Name</TableCell>
                <TableCell>Languages</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supervisors.map((supervisor, index) => {
                   const isEditing = editingSupervisorIndex === index;
                   const supervisorId = supervisor.id; 
                   return (
                   <TableRow key={supervisorId || `new-${index}`} hover>
                     <TableCell>{isEditing ? (<TextField size="small" fullWidth value={supervisor.name} onChange={(e) => handleSupervisorChange(index, 'name', e.target.value)} />) : supervisor.name}</TableCell>
                     <TableCell>
                        {isEditing ? (
                        <FormControl size="small" fullWidth>
                          <Select multiple value={supervisor.languages} onChange={(e) => handleSupervisorChange(index, 'languages', e.target.value)} renderValue={renderMultiChipValue} displayEmpty InputLabelProps={{ shrink: true }}>
                            {languageOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                        ) : (supervisor.languages || []).join(', ')}
                     </TableCell>
                     <TableCell align="center">
                        <Tooltip title={isEditing ? "Save" : "Edit"}>
                            <IconButton onClick={() => toggleSupervisorEdit(index)} color="primary" size="small">
                                {isEditing ? <SaveIcon /> : <EditIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton onClick={() => handleOpenDeleteDialog(supervisorId, 'supervisor', index)} color="error" size="small" disabled={isEditing}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                     </TableCell>
                   </TableRow>
                   );
               })}
            </TableBody>
          </Table>
        </TableContainer>
        <Button variant="outlined" onClick={addSupervisor} disabled={editingSupervisorIndex !== -1}>Add Supervisor</Button>
      </Paper>

      {/* --- 3. THERAPY SLOTS SECTION --- */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">Therapy Slots Allotment</Typography>
        
        <Grid container spacing={2}> 
          
          {/* Row 1: Unit, Client ID, Client Name */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Unit</InputLabel>
              <Select 
                value={therapySlots.unit} 
                label="Unit" 
                onChange={(e) => handleTherapySlotsChange('unit', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Unit')} 
                InputLabelProps={{ shrink: true }}
              >
                {unitOptions.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth 
              label="Client ID (OP/IP)" 
              value={therapySlots.client_id} 
              onChange={(e) => handleTherapySlotsChange('client_id', e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth 
              label="Client Name" 
              value={therapySlots.client_name} 
              onChange={(e) => handleTherapySlotsChange('client_name', e.target.value)} 
            />
          </Grid>

          {/* Row 2: Diagnosis, Device, Language */}
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth 
              label="Diagnosis" 
              placeholder="Enter Diagnosis" 
              value={therapySlots.diagnosis} 
              onChange={(e) => handleTherapySlotsChange('diagnosis', e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Device</InputLabel>
              <Select 
                value={therapySlots.device} 
                label="Device" 
                onChange={(e) => handleTherapySlotsChange('device', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Device')} 
                InputLabelProps={{ shrink: true }}
              >
                {deviceOptions.map((device) => <MenuItem key={device} value={device}>{device}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true} id="language-select-label">Language(s)</InputLabel>
              <Select 
                labelId="language-select-label" 
                multiple 
                value={therapySlots.language} 
                label="Language(s)" 
                onChange={(e) => handleTherapySlotsChange('language', e.target.value)}
                input={<OutlinedInput id="select-multiple-chip" label="Language(s)" />}
                renderValue={renderMultiChipValue} 
                displayEmpty 
                InputLabelProps={{ shrink: true }}
              >
                {languageOptions.map((lang) => <MenuItem key={lang} value={lang}>{lang}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 3: Session, Supervisor, Clinician Course */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Session</InputLabel>
              <Select 
                value={therapySlots.session} 
                label="Session" 
                onChange={(e) => handleTherapySlotsChange('session', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Session')} 
                InputLabelProps={{ shrink: true }}
              >
                {sessionOptions.map((ses) => <MenuItem key={ses} value={ses}>{ses}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Supervisor(s)</InputLabel>
              <Select 
                multiple
                value={therapySlots.supervisor_names} 
                label="Supervisor(s)" 
                onChange={(e) => handleTherapySlotsChange('supervisor_names', e.target.value)} 
                renderValue={renderMultiSupervisorValue}
                displayEmpty 
                InputLabelProps={{ shrink: true }}
              >
                {supervisors.map((sup) => (<MenuItem key={sup.id} value={sup.name}>{sup.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Clinician Course</InputLabel>
              <Select 
                value={therapySlots.clinicianCourse} 
                label="Clinician Course" 
                onChange={(e) => handleTherapySlotsChange('clinicianCourse', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Course')} 
                InputLabelProps={{ shrink: true }}
              >
                {courseOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 4: Clinician Year, Clinician Name, Room */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Clinician Year</InputLabel>
              <Select 
                value={therapySlots.clinicianYear} 
                label="Clinician Year" 
                disabled={!therapySlots.clinicianCourse} 
                onChange={(e) => handleTherapySlotsChange('clinicianYear', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Year')} 
                InputLabelProps={{ shrink: true }}
              >
                {(therapySlots.clinicianCourse === 'UG' ? ugYearOptions : pgYearOptions).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Clinician Name(s)</InputLabel>
              <Select 
                multiple
                value={therapySlots.clinicianNames} 
                label="Clinician Name(s)" 
                disabled={!therapySlots.clinicianYear} 
                onChange={(e) => handleTherapySlotsChange('clinicianNames', e.target.value)} 
                renderValue={renderMultiClinicianValue}
                displayEmpty 
                InputLabelProps={{ shrink: true }}
              >
                {students
                  .filter(student => student.year === therapySlots.clinicianYear)
                  .map((student) => (
                    <MenuItem key={student.id} value={student.name}>{student.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth 
              label="Room" 
              value={therapySlots.room} 
              onChange={(e) => handleTherapySlotsChange('room', e.target.value)} 
            />
          </Grid>

          {/* Row 5: Days, Timings */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Days</InputLabel>
              <Select 
                value={therapySlots.days_config} 
                label="Days" 
                onChange={(e) => handleTherapySlotsChange('days_config', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Days')} 
                InputLabelProps={{ shrink: true }}
              >
                {dayOptions.map((day) => <MenuItem key={day} value={day}>{day}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Timings</InputLabel>
              <Select 
                value={therapySlots.timings} 
                label="Timings" 
                onChange={(e) => handleTherapySlotsChange('timings', e.target.value)} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Timing')} 
                InputLabelProps={{ shrink: true }}
              >
                {timeSlots.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Row 6: Dates */}
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              type="date" 
              label="From Date" 
              value={therapySlots.fromDate} 
              onChange={(e) => handleTherapySlotsChange('fromDate', e.target.value)} 
              InputLabelProps={{ shrink: true }} 
            />
          </Grid>
       <Grid item xs={12} md={3}>
            <TextField 
              fullWidth 
              type="date" 
              label="End Date (1 Month Limit)" 
              value={attendance.endDate} 
              InputLabelProps={{ shrink: true }} 
              InputProps={{ readOnly: true }} 
            />
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleTherapySlotsSubmit} fullWidth size="large">Save Therapy Slot</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* --- 4. ATTENDANCE SECTION --- */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">Attendance</Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>Select **Start Date** to set a **1-month attendance period** (End Date auto-calculated).</Typography>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Unit</InputLabel>
              <Select 
                value={attendance.unit} 
                label="Unit" 
                onChange={handleAttendanceUnitChange} 
                displayEmpty 
                renderValue={(s) => renderValue(s, 'Select Unit')} 
                InputLabelProps={{ shrink: true }}
              >
                {unitOptions.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField 
              fullWidth 
              type="date" 
              label="Start Date" 
              value={attendance.startDate} 
              onChange={handleAttendanceStartDateChange} 
              InputLabelProps={{ shrink: true }} 
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField 
              fullWidth 
              type="date" 
              label="End Date (1 Month Limit)" 
              value={attendance.endDate} 
              InputLabelProps={{ shrink: true }} 
              InputProps={{ readOnly: true }} 
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="contained" color="secondary" fullWidth sx={{ height: '56px' }} onClick={fetchPatientDetails}>Fetch Details</Button>
          </Grid>
        </Grid>

        {attendance.fetchedPatients.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Assigned Clinicians</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="center">Present?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendance.fetchedPatients.map((row, index) => (
                  <TableRow key={row.client_id || index} hover>
                    <TableCell>{row.client_id}</TableCell>
                    <TableCell>{row.client_name}</TableCell>
                    <TableCell>
                      {Array.isArray(row.clinician_names) 
                        ? row.clinician_names.join(', ') 
                        : row.clinician_names}
                    </TableCell>
                    <TableCell>{row.days_config} at {row.timings}</TableCell>
                    <TableCell align="center"><Checkbox checked={row.isPresent || false} onChange={() => toggleAttendanceStatus(index)} color="success"/></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
  <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
        {attendance.fetchedPatients.length === 0 && (
          <Typography align="center" sx={{ py: 3, color: 'gray', border: '1px dashed #ccc' }}>
            No records fetched. Please select Unit & Date Range and click Fetch.
          </Typography>
        )}

        <Button variant="contained" color="primary" onClick={handleSaveAttendance} fullWidth sx={{ mt: 2 }} disabled={attendance.fetchedPatients.length === 0}>
          Save Attendance
        </Button>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, index: null, type: '', id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this {deleteDialog.type}? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, index: null, type: '', id: null })}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TherapyAllotment;